import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { blogApi } from '../lib/api';

export type BlogPostStatus = 'DRAFT' | 'PUBLISHED';

export type BlogLocale = 'en' | 'es' | 'pt';

export interface BlogPostTranslation {
  locale: BlogLocale;
  title: string;
  excerpt?: string | null;
  body: string;
}

export interface BlogChannel {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  postCount: number;
}

export interface BlogCategory {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  coverImageUrl: string | null;
  status: BlogPostStatus;
  channels: BlogChannel[];
  categories: BlogCategory[];
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  translations?: BlogPostTranslation[];
  availableLocales?: string[];
}

export interface BlogPostListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  status: BlogPostStatus;
  channels: BlogChannel[];
  categories: BlogCategory[];
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostListResponse {
  data: BlogPostListItem[];
  total: number;
  limit: number;
  page: number;
}

export interface CreateBlogPostPayload {
  title: string;
  slug?: string;
  excerpt?: string;
  body: string;
  coverImageUrl?: string;
  channelKeys: string[];
  categoryKeys?: string[];
  translations?: BlogPostTranslation[];
}

export interface UpdateBlogPostPayload {
  title?: string;
  slug?: string;
  excerpt?: string;
  body?: string;
  coverImageUrl?: string;
  channelKeys?: string[];
  categoryKeys?: string[];
  translations?: BlogPostTranslation[];
}

export interface UpsertChannelPayload {
  key: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpsertCategoryPayload {
  key: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

export function usePosts(params?: { limit?: number; page?: number; status?: string }) {
  return useQuery<BlogPostListResponse>({
    queryKey: ['blog', 'posts', params],
    queryFn: async () => {
      const { data } = await blogApi.get('/posts', { params });
      return data;
    },
  });
}

export function usePost(id: string) {
  return useQuery<BlogPost>({
    queryKey: ['blog', 'posts', id],
    queryFn: async () => {
      const { data } = await blogApi.get(`/posts/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBlogPostPayload) => {
      const { data } = await blogApi.post('/posts', payload);
      return data as BlogPost;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] }),
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBlogPostPayload }) => {
      const { data: result } = await blogApi.patch(`/posts/${id}`, data);
      return result as BlogPost;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] }),
  });
}

export function usePublishPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await blogApi.patch(`/posts/${id}/publish`);
      return data as BlogPost;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] }),
  });
}

export function useUnpublishPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await blogApi.patch(`/posts/${id}/unpublish`);
      return data as BlogPost;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] }),
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await blogApi.delete(`/posts/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'posts'] }),
  });
}

export function useChannels() {
  return useQuery<BlogChannel[]>({
    queryKey: ['blog', 'channels'],
    queryFn: async () => {
      const { data } = await blogApi.get('/channels');
      return data;
    },
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpsertChannelPayload) => {
      const { data } = await blogApi.post('/channels', payload);
      return data as BlogChannel;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'channels'] }),
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UpsertChannelPayload> }) => {
      const { data: result } = await blogApi.patch(`/channels/${id}`, data);
      return result as BlogChannel;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'channels'] }),
  });
}

export function useCategories() {
  return useQuery<BlogCategory[]>({
    queryKey: ['blog', 'categories'],
    queryFn: async () => {
      const { data } = await blogApi.get('/categories');
      return data;
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpsertCategoryPayload) => {
      const { data } = await blogApi.post('/categories', payload);
      return data as BlogCategory;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'categories'] }),
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UpsertCategoryPayload> }) => {
      const { data: result } = await blogApi.patch(`/categories/${id}`, data);
      return result as BlogCategory;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blog', 'categories'] }),
  });
}

export function useUploadBlogImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await blogApi.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as { url: string };
    },
  });
}
